import { ChevronDown, PlusCircle } from '@tamagui/lucide-icons';
import { Row } from '@unistocks/components/layout/flex';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Page } from '@unistocks/components/page/page';
import { Select } from '@unistocks/components/select/select';
import { selectSelectedSavingsGoalId } from '@unistocks/features/savings-goals/store/savings-goals.slice';
import { t } from '@unistocks/localization';
import { ListResultDtoOfLookupDtoOfGuid } from '@unistocks/proxy/axios-client';
import { useSavingsGoalsGetListQuery } from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { SelectOption } from '@unistocks/types';
import { useRouter } from 'expo-router';
import React, { PropsWithChildren, useCallback } from 'react';
import { isWeb, Spinner } from 'tamagui';

export const SavingsGoalHeaderTitle: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const selected = useAppSelector(selectSelectedSavingsGoalId) ?? '';
  const {
    data: savingsGoals,
    isSuccess,
    isLoading
  } = useSavingsGoalsGetListQuery({
    structuralSharing: false,
    select: useCallback(
      ({ items }: ListResultDtoOfLookupDtoOfGuid) =>
        items
          .map(
            ({ id, displayName }) =>
              ({ key: displayName, value: id, icon: <UnistocksCircle /> }) as SelectOption
          )
          .concat([
            {
              key: t('::AddWhat', { 0: t('::SavingsGoal').toLocaleLowerCase() }),
              value: 'add',
              icon: <PlusCircle color={'$success'} />
            }
          ]),
      []
    )
  });

  const onSelect = useCallback(
    (value: string) => {
      if (value === 'add') {
        return router.navigate('/(tabs)/(menu)/savings-goals/create');
      }

      router.setParams({ id: value });
    },
    [router]
  );

  if (isLoading) return <Spinner color={'$headingFont'} />;

  if (isSuccess) {
    if (isWeb) {
      return <Page.Heading>{children}</Page.Heading>;
    }

    return (
      <Select
        options={savingsGoals}
        value={selected}
        onChange={onSelect}
        isRequired
        headerTitle={t('::SelectWhat', { 0: t('::SavingsGoal').toLocaleLowerCase() })}
        trigger={
          <Row gap={'$1'} alignItems={'center'}>
            <Page.Heading>{children}</Page.Heading>
            <ChevronDown color={'$headingFont'} />
          </Row>
        }
      />
    );
  }

  return null;
};
